/**
 * useGroups — TanStack Query hooks for per-admin Empresa/Sector grouping.
 *
 * Pattern parallels `useAgents.ts`. Mutations invalidate both the groups
 * query keys and the agents query key, since assigning/moving an agent
 * changes GET /api/v1/agents payloads.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { groupsApi } from '@/lib/api/groups';
import type { UpdateAssignmentRequest } from '@/types/groups';

const KEYS = {
  groups: (include?: 'subgroups'): readonly unknown[] =>
    include ? ['groups', { include }] : ['groups'],
  group: (id: string) => ['groups', id] as const,
  agents: ['agents'] as const,
};

/**
 * List the calling admin's groups.
 * @param include  When "subgroups", the server embeds subgroups + agentCount
 *                 via 2 batch queries (no N+1).
 */
export const useGroups = (include?: 'subgroups') =>
  useQuery({
    queryKey: KEYS.groups(include),
    queryFn: () => groupsApi.listGroups(include),
    refetchInterval: 30_000, // fallback for WS
    staleTime: 10_000,
  });

export const useGroup = (id: string | null) =>
  useQuery({
    queryKey: KEYS.group(id ?? ''),
    queryFn: () => groupsApi.getGroup(id!),
    enabled: !!id,
  });

export const useCreateGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color?: string; icon?: string; position?: number }) =>
      groupsApi.createGroup(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: KEYS.agents });
    },
  });
};

export const useUpdateGroup = (groupId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Partial<{ name: string; color: string | null; icon: string | null; position: number }>
    ) => groupsApi.updateGroup(groupId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: KEYS.agents });
    },
  });
};

export const useDeleteGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => groupsApi.deleteGroup(groupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: KEYS.agents });
    },
  });
};

// ── Subgroups ────────────────────────────────────────────────────────────────

export const useCreateSubgroup = (groupId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; position?: number }) =>
      groupsApi.createSubgroup(groupId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });
};

export const useUpdateSubgroup = (groupId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subgroupId,
      body,
    }: {
      subgroupId: string;
      body: { name?: string; position?: number };
    }) => groupsApi.updateSubgroup(groupId, subgroupId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });
};

export const useDeleteSubgroup = (groupId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subgroupId: string) => groupsApi.deleteSubgroup(groupId, subgroupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: KEYS.agents });
    },
  });
};

// ── Agent assignment ────────────────────────────────────────────────────────

/**
 * PUT /api/v1/agents/{id}/assignment. Pass `{}` (or both fields null) to
 * clear the assignment (agent → "(Sin grupo)").
 *
 * Backend returns 409 on cross-user attempt — surfaced as a mutation error
 * which the caller (typically the GroupSelect component) renders as a toast.
 */
export const useUpdateAgentAssignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, body }: { agentId: string; body: UpdateAssignmentRequest }) =>
      groupsApi.updateAgentAssignment(agentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.agents });
    },
  });
};
